/**
 * React Starter Kit (https://www.reactstarterkit.com/)
 *
 * Copyright © 2014-present Kriasoft, LLC. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE.txt file in the root directory of this source tree.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import { Provider as ReduxProvider } from 'react-redux';
import { ApolloProvider } from 'react-apollo';
import DateFnsUtils from '@date-io/date-fns';
import { isEmpty } from 'shared/utilities/utils';

// Internationalization resources
import i18n from 'shared/utilities/i18n';
import { I18nextProvider } from 'react-i18next';

import SnackBarProviderWrapper from 'shared/components/Providers/SnackBarProviderWrapper';

const ContextType = {
  // Enables critical path CSS rendering
  // https://github.com/kriasoft/isomorphic-style-loader
  insertCss: PropTypes.func.isRequired,
  // Universal HTTP client
  fetch: PropTypes.func.isRequired,
  pathname: PropTypes.string.isRequired,
  query: PropTypes.object,
  // Integrate Redux
  // http://redux.js.org/docs/basics/UsageWithReact.html
  store: PropTypes.object.isRequired,
  // Apollo Client
  client: PropTypes.object.isRequired,
  // Local browser storage (if client side rendered)
  localStorage: PropTypes.object,
  developmentEnvironment: PropTypes.string.isRequired,
};

export const DevelopmentEnvironmentContext = React.createContext();

/**
 * The top-level React component setting context (global) variables
 * that can be accessed from all the child components.
 *
 * https://facebook.github.io/react/docs/context.html
 *
 * Usage example:
 *
 *   const context = {
 *     history: createBrowserHistory(),
 *     store: createStore(),
 *   };
 *
 *   ReactDOM.render(
 *     <App context={context}>
 *       <Layout>
 *         <LandingPage />
 *       </Layout>
 *     </App>,
 *     container,
 *   );
 */
class App extends React.PureComponent {
  static propTypes = {
    context: PropTypes.shape(ContextType).isRequired,
    children: PropTypes.element.isRequired,
  };

  static childContextTypes = ContextType;

  getChildContext() {
    return this.props.context;
  }

  render() {
    const { client, store } = this.props.context;

    // On App initialization, if a user is logged in and has a language preferecnce,
    // set the i18n default language. Otherwise, default to English.
    const { currentUser } = store.getState().subscriptions;
    if (currentUser) {
      if (!isEmpty(currentUser.profile.languagePreference)) {
        i18n.changeLanguage(currentUser.profile.languagePreference);
      }
    }

    // NOTE: If you need to add or modify header, footer etc. of the app,
    // please do that inside the Layout component.
    return (
      <I18nextProvider i18n={i18n}>
        <ReduxProvider store={store}>
          <SnackBarProviderWrapper>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <ApolloProvider client={client}>
                <DevelopmentEnvironmentContext.Provider
                  value={this.props.context.developmentEnvironment}
                >
                  {this.props.children}
                </DevelopmentEnvironmentContext.Provider>
              </ApolloProvider>
            </MuiPickersUtilsProvider>
          </SnackBarProviderWrapper>
        </ReduxProvider>
      </I18nextProvider>
    );
  }
}

export default App;
